import * as React from 'react';
import { useEventCallback, useForkRef } from '../../hooks';
import { FixMeLater } from '../../utils';
import { useStyles } from './ButtonBase.styles';
import { useFieldBase } from '../FieldBase/FieldBase.context';

export type ButtonType = 'submit' | 'reset' | 'button';
export interface ButtonBaseProps {
    /**
     * The content of the component.
     */
    children?: React.ReactNode;

    /**
     * The component used for the root node.
     */
    component?: React.ElementType;

    /**
     * If `true`, the component will be disabled.
     */
    disabled?: boolean;

    /**
     * The URL that the button will navigate to. If provided, the button will be rendered as an anchor.
     */
    href?: string;

    /**
     * Callback fired when the element loses focus.
     * @param {React.FocusEvent} event The event source of the callback.
     */
    onBlur?: React.FocusEventHandler;

    /**
     * Callback fired when the element has been clicked.
     * @param {React.SyntheticEvent} event The event source of the callback.
     */
    onClick?: (event: React.SyntheticEvent) => void;

    /**
     * Callback fired when the element receives focus.
     * @param {React.FocusEvent} event The event source of the callback.
     */
    onFocus?: React.FocusEventHandler;

    /**
     * Callback fired when a key is pressed while the element has focus.
     * @param {React.KeyboardEventHandler} event The event source of the callback.
     */
    onKeyDown?: React.KeyboardEventHandler;

    /**
     * The tabindex attribute applied to the element.
     * @default 0
     */
    tabIndex?: number;

    /**
     * The type attribute applied to the `button` element.
     * @default button
     */
    type?: ButtonType;

    /**
     * @internal
     * This prop is used internally by FieldBase. Do not set it manually.
     */
    isFieldBaseControl?: boolean;

    /**
     * Permit additional properties which will be passed to the root node.
     */
    [x: string]: FixMeLater;
}

export const ButtonBase: React.FC<React.PropsWithChildren<React.PropsWithChildren<ButtonBaseProps>>> = React.forwardRef(
    function ButtonBase(props: ButtonBaseProps, ref: React.ForwardedRef<FixMeLater>): React.JSX.Element {
        const {
            children,
            component = 'button',
            disabled = false,
            href,
            onBlur,
            onClick,
            onFocus,
            onKeyDown,
            tabIndex = 0,
            type = 'button',
            isFieldBaseControl = false,
            ...other
        } = props;

        const { setFocused, controlRef } = useFieldBase();

        const elementRef = React.useRef<HTMLButtonElement | null>(null);
        const handleRefIntermediary = useForkRef(
            elementRef,
            isFieldBaseControl ? (controlRef as ((instance: HTMLButtonElement | null) => void) | null) : null
        );
        const handleRef = useForkRef(handleRefIntermediary, ref);

        const { styles } = useStyles();

        const buttonProps: {
            type?: ButtonType;
            disabled?: boolean;
            role?: string;
            'aria-disabled'?: boolean;
        } = {};

        let ComponentProp = component;
        if (href) {
            ComponentProp = 'a';
        }

        if (ComponentProp === 'button') {
            buttonProps.type = type || 'button';
            buttonProps.disabled = disabled;
        } else {
            // Component is a non-native button element
            if (!href) {
                buttonProps.role = 'button';
            }
            if (disabled) {
                buttonProps['aria-disabled'] = disabled;
            }
        }

        const handleBlur = useEventCallback((event: React.FocusEvent): void => {
            onBlur?.(event);
            setFocused?.(false);
        });

        const handleClick = useEventCallback((event: React.MouseEvent): void => {
            if (!disabled) {
                onClick?.(event);
            }
        });

        const handleFocus = useEventCallback((event: React.FocusEvent): void => {
            onFocus?.(event);
            setFocused?.(true);
        });

        const handleKeyDown = useEventCallback((event: React.KeyboardEvent) => {
            onKeyDown?.(event);

            // Keyboard accessibility for non interactive elements
            if (
                event.target === event.currentTarget &&
                ComponentProp &&
                ComponentProp !== 'button' &&
                ComponentProp !== 'a' &&
                (event.key === 'Enter' || event.key === ' ') &&
                !disabled
            ) {
                event.preventDefault();
                onClick?.(event);
            }
        });

        return (
            <ComponentProp
                ref={handleRef}
                css={styles.root}
                onBlur={handleBlur}
                onClick={handleClick}
                onFocus={handleFocus}
                onKeyDown={handleKeyDown}
                tabIndex={disabled ? -1 : tabIndex}
                href={href}
                {...buttonProps}
                {...other}
            >
                {children}
            </ComponentProp>
        );
    }
);
